/**
 * MediaService — the single entry point the editor uses for media.
 *
 * Orchestrates the pluggable parts (storage providers, analyzer, derivative generator, cache)
 * and exposes a reactive runtime view per media item (`useMediaRuntime`) for the UI and a
 * synchronous resolver for the engine. It never decides *where* media lives; providers do.
 */
import { WaveformSchema, newId, type MediaAsset, type MediaOrigin, type Project } from '@ve/core';
import type { MediaInput, MediaSources } from '@ve/engine';
import { create } from 'zustand';
import type { ProviderRegistry } from './registry.ts';
import {
  MediaAccessError,
  UnsupportedMediaError,
  type CachedDerivative,
  type DerivativeCache,
  type DerivativeGenerator,
  type DerivativeKind,
  type ImportSource,
  type MediaAnalyzer,
  type MediaRuntime,
  type ProviderAccess,
} from './types.ts';

export interface ActiveImport {
  id: string;
  name: string;
  phase: 'copying' | 'analyzing';
  progress: number;
}

interface MediaState {
  projectId: string | null;
  runtime: Record<string, MediaRuntime>;
  /** Access state per provider used by the open project (and the import provider). */
  access: Record<string, ProviderAccess>;
  imports: Record<string, ActiveImport>;
}

export const useMediaRuntime = create<MediaState>()(() => ({
  projectId: null,
  runtime: {},
  access: {},
  imports: {},
}));

const DERIVATIVE_PRIORITY: Record<DerivativeKind, number> = {
  thumbnail: 0,
  waveform: 1,
  filmstrip: 2,
  proxy: 3,
};

interface Task {
  mediaId: string;
  kind: DerivativeKind;
}

export interface MediaServiceDeps {
  registry: ProviderRegistry;
  analyzer: MediaAnalyzer;
  derivatives: DerivativeGenerator;
  cache: DerivativeCache;
}

function baseName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

/**
 * Bumped when a generator's output changes, so cached derivatives made by the old one are replaced.
 * Proxies 2: HDR media is tone-mapped by the compositor instead of the browser.
 */
const DERIVATIVE_REVISIONS: Partial<Record<DerivativeKind, number>> = { proxy: 2 };

function derivativeFingerprint(asset: MediaAsset, input: MediaInput, kind: DerivativeKind): string {
  const revision = DERIVATIVE_REVISIONS[kind];
  const base = fingerprintOf(asset, input);
  return revision ? `${base}-r${revision}` : base;
}

function fingerprintOf(asset: MediaAsset, input: MediaInput): string {
  if (input instanceof Blob) {
    const lastModified = input instanceof File ? input.lastModified : (asset.location.lastModified ?? 0);
    return `${input.size}-${lastModified}`;
  }
  return `${asset.location.provider}-${asset.location.ref}`;
}

export class MediaService {
  private project: Project | null = null;
  /** Bumped on attach/detach so late async results from a previous project are ignored. */
  private generation = 0;
  private queue: Task[] = [];
  private running: { task: Task; controller: AbortController } | null = null;
  private objectUrls = new Map<string, string[]>();
  /**
   * Assets imported but not yet in the document the service knows (the caller adds them in an
   * edit after `importSources` resolves). Derivative jobs must still find them.
   */
  private incoming = new Map<string, MediaAsset>();

  constructor(private readonly deps: MediaServiceDeps) {}

  get registry(): ProviderRegistry {
    return this.deps.registry;
  }

  private get state() {
    return useMediaRuntime.getState();
  }

  private patchRuntime(mediaId: string, patch: Partial<MediaRuntime>): void {
    const current = this.state.runtime[mediaId] ?? { availability: 'loading', derivatives: {} };
    useMediaRuntime.setState({ runtime: { ...this.state.runtime, [mediaId]: { ...current, ...patch } } });
  }

  // -------------------------------------------------------------------------------------------
  // Project lifecycle
  // -------------------------------------------------------------------------------------------

  async attach(project: Project): Promise<void> {
    this.detach();
    this.project = project;
    useMediaRuntime.setState({ projectId: project.id, runtime: {}, access: {}, imports: {} });
    await this.refreshAccess();
    await this.openAll(Object.values(project.media));
  }

  detach(): void {
    this.generation++;
    this.queue = [];
    this.running?.controller.abort();
    this.running = null;
    for (const urls of this.objectUrls.values()) urls.forEach((u) => URL.revokeObjectURL(u));
    this.objectUrls.clear();
    this.incoming.clear();
    this.project = null;
    useMediaRuntime.setState({ projectId: null, runtime: {}, access: {}, imports: {} });
  }

  /** Keeps runtime state in step with the document (media added, e.g. by redo, or removed). */
  sync(project: Project): void {
    if (!this.project || project.id !== this.project.id) return;
    this.project = project;
    for (const id of this.incoming.keys()) if (project.media[id]) this.incoming.delete(id);
    const added = Object.values(project.media).filter((a) => !this.state.runtime[a.id]);
    if (added.length > 0) void this.openAll(added);
  }

  /** Re-checks provider access without prompting. */
  async refreshAccess(): Promise<void> {
    const project = this.project;
    if (!project) return;
    const ids = new Set([
      this.registry.importProvider.id,
      ...Object.values(project.media).map((a) => a.location.provider),
    ]);
    const access: Record<string, ProviderAccess> = {};
    for (const id of ids) {
      const provider = this.registry.get(id);
      access[id] = provider
        ? await provider.getAccess(project.id)
        : {
            state: 'unsupported',
            reason: `Unknown storage “${id}” — this project was made by a newer version`,
          };
    }
    useMediaRuntime.setState({ access });
  }

  /**
   * Connects (or reconnects / relocates) a provider. Must be called from a user gesture.
   * Afterwards every offline media item of that provider is reopened.
   */
  async connect(
    providerId = this.registry.importProvider.id,
    options: { relocate?: boolean } = {},
  ): Promise<ProviderAccess> {
    const project = this.project;
    const provider = this.registry.get(providerId);
    if (!project || !provider) throw new Error(`Unknown storage provider ${providerId}`);
    const access = await provider.requestAccess(project.id, options);
    useMediaRuntime.setState({ access: { ...this.state.access, [providerId]: access } });
    if (access.state === 'ready') {
      const assets = Object.values(project.media).filter(
        (a) =>
          a.location.provider === providerId &&
          (options.relocate || this.state.runtime[a.id]?.availability !== 'online'),
      );
      await this.openAll(assets);
    }
    return access;
  }

  // -------------------------------------------------------------------------------------------
  // Import
  // -------------------------------------------------------------------------------------------

  /**
   * Stores files through the import provider and analyses them. Returns the new assets, aligned
   * with `sources` (the caller adds them to the document). Failed items are reported via `onError`
   * and come back as `null`.
   */
  async importSources(
    sources: Array<{ source: ImportSource; origin?: MediaOrigin }>,
    onError: (name: string, err: unknown) => void,
    onItemProgress?: (index: number, fraction: number) => void,
    onWarning?: (name: string, warning: string) => void,
  ): Promise<Array<MediaAsset | null>> {
    const project = this.project;
    if (!project) return sources.map(() => null);
    const provider = this.registry.importProvider;
    const results = await Promise.all(
      sources.map(async ({ source, origin }, index) => {
        const importId = newId('imp');
        const name = source.kind === 'handle' ? source.handle.name : source.name;
        const setImport = (patch: Partial<ActiveImport> | null) => {
          const imports = { ...this.state.imports };
          if (patch === null) delete imports[importId];
          else
            imports[importId] = {
              ...{ id: importId, name, phase: 'copying', progress: 0 },
              ...imports[importId],
              ...patch,
            };
          useMediaRuntime.setState({ imports });
        };
        setImport({});
        try {
          const stored = await provider.importFile(project.id, source, {
            onProgress: (progress) => {
              setImport({ progress });
              onItemProgress?.(index, progress);
            },
          });
          setImport({ phase: 'analyzing', progress: 1 });
          const { warnings, ...probe } = await this.deps.analyzer.probe(stored.file, stored.name);
          for (const warning of warnings ?? []) onWarning?.(stored.name, warning);
          const asset: MediaAsset = {
            id: newId('med'),
            name: baseName(stored.name),
            ...probe,
            origin: origin ?? { type: 'file', fileName: stored.name },
            location: stored.location,
            importedAt: new Date().toISOString(),
          };
          this.incoming.set(asset.id, asset);
          this.setOnline(asset, stored.file);
          return asset;
        } catch (err) {
          onError(name, err);
          return null;
        } finally {
          setImport(null);
        }
      }),
    );
    return results;
  }

  // -------------------------------------------------------------------------------------------
  // Engine & UI access
  // -------------------------------------------------------------------------------------------

  /** Synchronous resolver for the engine. */
  resolve = (mediaId: string): MediaSources | undefined => {
    const runtime = this.state.runtime[mediaId];
    return runtime?.availability === 'online' ? runtime.sources : undefined;
  };

  /** Re-runs failed derivatives (and reopens the file if it was offline). */
  async retry(mediaId: string): Promise<void> {
    const asset = this.assetOf(mediaId);
    if (!asset) return;
    if (this.state.runtime[mediaId]?.availability !== 'online') {
      await this.openAll([asset]);
      return;
    }
    this.schedule(asset, (kind) => this.state.runtime[mediaId]?.derivatives[kind] === 'failed');
  }

  // -------------------------------------------------------------------------------------------
  // Opening & derivatives
  // -------------------------------------------------------------------------------------------

  private async openAll(assets: MediaAsset[]): Promise<void> {
    const generation = this.generation;
    const project = this.project;
    if (!project) return;
    await Promise.all(
      assets.map(async (asset) => {
        this.patchRuntime(asset.id, { availability: 'loading', message: undefined });
        const provider = this.registry.get(asset.location.provider);
        try {
          if (!provider)
            throw new MediaAccessError(`Unknown storage “${asset.location.provider}”`, 'unsupported');
          const input = await provider.open(project.id, asset.location);
          if (generation !== this.generation) return;
          this.setOnline(asset, input);
        } catch (err) {
          if (generation !== this.generation) return;
          const offline = err instanceof MediaAccessError && err.reason !== 'changed';
          this.patchRuntime(asset.id, {
            availability: offline ? 'offline' : 'error',
            message: err instanceof Error ? err.message : String(err),
            sources: undefined,
          });
        }
      }),
    );
  }

  private setOnline(asset: MediaAsset, input: MediaInput): void {
    const previous = this.state.runtime[asset.id];
    this.patchRuntime(asset.id, {
      availability: 'online',
      message: undefined,
      // Keep an existing proxy only if it was made from this very input.
      sources: {
        master: input,
        proxy: previous?.sources?.master === input ? previous.sources.proxy : undefined,
      },
      derivatives: Object.fromEntries(this.deps.derivatives.plan(asset).map((k) => [k, 'pending'])),
    });
    this.schedule(asset, () => true);
  }

  private schedule(asset: MediaAsset, include: (kind: DerivativeKind) => boolean): void {
    for (const kind of this.deps.derivatives.plan(asset)) {
      if (!include(kind)) continue;
      if (this.queue.some((t) => t.mediaId === asset.id && t.kind === kind)) continue;
      this.queue.push({ mediaId: asset.id, kind });
      this.setDerivative(asset.id, kind, 'pending');
    }
    this.queue.sort((a, b) => DERIVATIVE_PRIORITY[a.kind] - DERIVATIVE_PRIORITY[b.kind]);
    void this.pump();
  }

  private setDerivative(
    mediaId: string,
    kind: DerivativeKind,
    state: MediaRuntime['derivatives'][DerivativeKind],
  ) {
    const current = this.state.runtime[mediaId];
    if (!current) return;
    this.patchRuntime(mediaId, { derivatives: { ...current.derivatives, [kind]: state } });
  }

  private async pump(): Promise<void> {
    if (this.running) return;
    const task = this.queue.shift();
    if (!task) return;
    const generation = this.generation;
    const controller = new AbortController();
    this.running = { task, controller };
    try {
      await this.runTask(task, controller.signal);
    } finally {
      if (generation === this.generation) {
        this.running = null;
        void this.pump();
      }
    }
  }

  private assetOf(mediaId: string): MediaAsset | undefined {
    return this.project?.media[mediaId] ?? this.incoming.get(mediaId);
  }

  private async runTask({ mediaId, kind }: Task, signal: AbortSignal): Promise<void> {
    const asset = this.assetOf(mediaId);
    const runtime = this.state.runtime[mediaId];
    const input = runtime?.sources?.master;
    if (!asset || !input || runtime.availability !== 'online') return;
    const key = { mediaId, kind, fingerprint: derivativeFingerprint(asset, input, kind) };
    this.setDerivative(mediaId, kind, 'processing');
    try {
      let cached = await this.deps.cache.get(key);
      if (!cached) {
        const result = await this.deps.derivatives.generate(kind, asset, input, {
          signal,
          onProgress: (progress) => this.patchRuntime(mediaId, { progress }),
        });
        cached = await this.deps.cache.put(key, result);
      }
      if (signal.aborted) return;
      await this.apply(mediaId, kind, cached);
      this.setDerivative(mediaId, kind, 'ready');
    } catch (err) {
      if (signal.aborted) return;
      console.warn(`Generating ${kind} for ${asset.name} failed`, err);
      this.setDerivative(mediaId, kind, err instanceof UnsupportedMediaError ? 'skipped' : 'failed');
    } finally {
      this.patchRuntime(mediaId, { progress: undefined });
    }
  }

  private objectUrl(mediaId: string, blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.set(mediaId, [...(this.objectUrls.get(mediaId) ?? []), url]);
    return url;
  }

  private async apply(mediaId: string, kind: DerivativeKind, cached: CachedDerivative): Promise<void> {
    const runtime = this.state.runtime[mediaId];
    if (!runtime) return;
    switch (kind) {
      case 'thumbnail':
        this.patchRuntime(mediaId, { thumbnailUrl: this.objectUrl(mediaId, cached.file) });
        break;
      case 'filmstrip':
        if (cached.filmstrip) {
          this.patchRuntime(mediaId, {
            filmstrip: { url: this.objectUrl(mediaId, cached.file), info: cached.filmstrip },
          });
        }
        break;
      case 'waveform':
        this.patchRuntime(mediaId, { waveform: WaveformSchema.parse(JSON.parse(await cached.file.text())) });
        break;
      case 'proxy':
        if (runtime.sources)
          this.patchRuntime(mediaId, { sources: { ...runtime.sources, proxy: cached.file } });
        break;
    }
  }
}
